import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Card, CardBody, Center, Heading, Icon, List, ListIcon, ListItem,
  Text, Tooltip, VStack, useColorModeValue,
} from "@chakra-ui/react";
import { FiCheck, FiLock } from "react-icons/fi";

/**
 * The paywall, made honest.
 *
 * SHELV_PRO is granted by the Shelv Pro SubscriptionProduct on activation —
 * there is exactly one billing path (`/subscribe/:slug`), never a
 * module-specific one. This screen renders where a Pro page would, so a free
 * kitchen sees what it's missing rather than a 404 or a dead nav item.
 */
/**
 * Beta-only discount applied to the in-app upgrade link. Remove this and the
 * `?coupon=` below the moment Shelv sells to a real restaurant.
 */
const SHELV_BETA_COUPON = "SHELVFOUNDER";

const PRO_FEATURES = [
  "Meal calendar — plan any recipe onto any day",
  "Scaling — one recipe, any number of covers",
  "Budgeting — what the week's orders should cost",
  "Actual vs predicted spend",
  "Meal feedback from staff and diners",
  "Chef's plated-meal photo, every service",
  "Supplier orders generated from the plan",
  "Unlimited recipe imports",
];

const ProLocked: React.FC<{ feature?: string }> = ({ feature }) => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.700");
  const subtle = useColorModeValue("gray.600", "gray.400");

  return (
    <Center py={{ base: 8, md: 14 }} px={4}>
      <Card bg={cardBg} borderWidth="1px" borderColor={border} maxW="lg" w="100%">
        <CardBody>
          <VStack spacing={5} align="stretch">
            <Box textAlign="center">
              <Icon as={FiLock} boxSize={8} color={subtle} mb={2} />
              <Heading size="md">{feature ? `${feature} is part of Shelv Pro` : "That's part of Shelv Pro"}</Heading>
              <Text fontSize="sm" color={subtle} mt={2}>
                Your recipe book stays free, always. Pro adds the tools for running service.
              </Text>
            </Box>

            <List spacing={2}>
              {PRO_FEATURES.map((f) => (
                <ListItem key={f} fontSize="sm">
                  <ListIcon as={FiCheck} color="green.500" />
                  {f}
                </ListItem>
              ))}
            </List>

            {/* The founder coupon rides on the IN-APP link only, so anyone
                clicking Upgrade from inside Shelv gets it pre-applied while the
                bare /subscribe/shelv-pro URL stays full price and can be handed
                to a real restaurant unchanged.

                ⚠️ DELETE the ?coupon= param when Shelv starts selling for real.
                A discount that lives on the product itself would quietly apply
                to every paying customer forever. */}
            <Tooltip label="Opens the subscription checkout — the founder discount is applied for you">
              <Button
                colorScheme="green"
                onClick={() => navigate(`/subscribe/shelv-pro?coupon=${SHELV_BETA_COUPON}`)}
              >
                See Shelv Pro
              </Button>
            </Tooltip>
            <Button variant="ghost" size="sm" onClick={() => navigate("/shelv")}>
              Back to the shelf
            </Button>
          </VStack>
        </CardBody>
      </Card>
    </Center>
  );
};

export default ProLocked;
